import { getGatedIdacBackendUrl } from '../utilities/env';
import {
  Action,
  ConfirmationErrorCode,
  ConfirmationFieldError,
  CsrfToken,
  ThunkAction,
  URLParameters,
} from '../types';
import {
  buildMarketingConsent,
  ConsentCheckboxState,
  MarketingConsent,
} from '../utilities/marketing-consent';
import { LoginType, InHouseSocialLoginType } from '../utilities/social-login';
import { State } from '../reducers';
import { submitFormHackReplacement } from '../utilities/submit-form';
import { ChallengeRequest } from '../utilities/recaptcha';

import queryString from 'query-string';
import { getCastleRequestToken } from '../castle';
import { restoreOriginalRedirectUrlParams } from '../utilities/redirect-url-params';
import { captureTaggedException } from '../utilities/analytics/error-reporting';

/**
 * Despite this file's name, it only handles the confirmation for creating new accounts via a social provider, rather than
 * confirmations for all accounts. (e.g. it does not handle password accounts)
 *
 * This flow occurs during signup for a fresh Atlassian account when authenticated via a social provider.
 */

export type ConfirmationAction =
  | { type: 'CONFIRMATION_REQUEST_FAILED'; error: ConfirmationErrorCode }
  | {
      type: 'CONFIRMATION_REQUEST_FAILED_WITH_REDIRECT';
      error: ConfirmationErrorCode;
      redirectTo: string;
    }
  | { type: 'CONFIRMATION_SHOW_FIELD_ERROR'; errors: ConfirmationFieldError }
  | { type: 'CONFIRMATION_RESET' }
  | { type: 'CONFIRMATION_REQUEST' }
  | { type: 'CONFIRMATION_REQUEST_SUCCESS'; redirectTo: string }
  | { type: 'CONFIRMATION_RESEND_SUCCESS'; mfaToken: string }
  | { type: 'CONFIRMATION_STATE_UPDATE'; backendState: Partial<State['createAccount']> };

export const reset = (): Action => ({
  type: 'CONFIRMATION_RESET',
});

export const confirmationRequest = (): Action => ({
  type: 'CONFIRMATION_REQUEST',
});

export const confirmationRequestSucceeded = (redirectTo: string): Action => ({
  type: 'CONFIRMATION_REQUEST_SUCCESS',
  redirectTo,
});

export const confirmationRequestFailed = (error: ConfirmationErrorCode): Action => ({
  type: 'CONFIRMATION_REQUEST_FAILED',
  error,
});

export const confirmationRequestFailedWithRedirect = (
  error: ConfirmationErrorCode,
  redirectTo: string
): Action => ({
  type: 'CONFIRMATION_REQUEST_FAILED_WITH_REDIRECT',
  error,
  redirectTo,
});

/**
 * This ensures when making a request to the backend and it returns state, that the FE state reflects that
 * This previously was handled implicitly because the entire page would reload (because SIS handled generating the entire page, including the FE state)
 * Which no longer happens because we're making fetch requests instead.
 */
export const confirmationRequestStateUpdate = (
  backendState: Partial<State['createAccount']>
): Action => ({
  type: 'CONFIRMATION_STATE_UPDATE',
  backendState,
});

export const showFieldErrors = (errors: ConfirmationFieldError): Action => ({
  type: 'CONFIRMATION_SHOW_FIELD_ERROR',
  errors,
});

export const resendSucceeded = (mfaToken: string): Action => ({
  type: 'CONFIRMATION_RESEND_SUCCESS',
  mfaToken,
});

interface BaseConfirmationParams {
  loginType: LoginType;
  source?: string;
  displayName?: string;
  marketingConsentState?: ConsentCheckboxState;
  privacyPolicyText: string;
  userFlow?: string;
}

export interface InHouseSocialConfirmationParams extends BaseConfirmationParams {
  loginType: InHouseSocialLoginType;
  confirmationToken: string;
  mfaToken?: string;
  hashedCsrfToken: CsrfToken;
  code?: string;
  challengeRequest?: ChallengeRequest | null;
}
interface ConfirmationJsonPayload {
  source?: string;
  fullName?: string;
  marketingConsent?: MarketingConsent;
  code?: string;
  atlassianAccountCreationConfirmed: true; // This should not be sent in the verify-only case but will be ignored on the Auth0 side. Kept to minimise changes.
}

interface InHouseSocialConfirmationJsonPayload extends ConfirmationJsonPayload {
  loginType: InHouseSocialLoginType;
  csrfToken: CsrfToken;
  confirmationToken: string;
  mfaToken?: string;
  code?: string;
  challengeResponse?: ChallengeRequest | null;
  castleToken?: string | null;
  // Native signup handoff token (from the `nativeHandoff` URL param). When
  // present and valid, SIS may skip reCAPTCHA for the user, mirroring the
  // password signup flow.
  nativeHandoffSignupToken?: string;
}

// Resolved against the current origin because this endpoint also returns relative redirects. A value
// that will not parse is left to throw so the outer catch fails the request instead of navigating to it.
const hasInvalidNativeSignupErrorCode = (redirectTo: string): boolean =>
  new URL(redirectTo, window.location.origin).searchParams.get('errorCode') ===
  'invalidNativeSignup';

const buildBaseConfirmationJsonPayload = (
  confirmationParams: BaseConfirmationParams,
  state: State
): ConfirmationJsonPayload => {
  const { source, displayName, marketingConsentState, privacyPolicyText } = confirmationParams;

  const payload: ConfirmationJsonPayload = {
    fullName: displayName,
    source,
    atlassianAccountCreationConfirmed: true,
  };

  if (marketingConsentState) {
    const marketingConsent = buildMarketingConsent(
      state.marketingConsent.config,
      marketingConsentState,
      privacyPolicyText
    );
    if (marketingConsent) {
      payload.marketingConsent = marketingConsent;
    }
  }

  return payload;
};

export const inHouseSocialConfirmation =
  (confirmationParams: InHouseSocialConfirmationParams): ThunkAction =>
  async (dispatch, getState) => {
    const { loginType, confirmationToken, mfaToken, hashedCsrfToken, code, challengeRequest } =
      confirmationParams;

    dispatch(confirmationRequest());

    const castleToken = await getCastleRequestToken([
      'capability-group--signup',
      'capability-name--signup',
    ]);

    const params: URLParameters = queryString.parse(location.search);
    const { nativeHandoff: nativeHandoffSignupToken } = params;

    let responseStatus: number | undefined;
    try {
      const payload: InHouseSocialConfirmationJsonPayload = {
        ...buildBaseConfirmationJsonPayload(confirmationParams, getState()),
        challengeResponse: challengeRequest, // renaming as SIS expects 'challengeResponse'
        loginType,
        csrfToken: hashedCsrfToken,
        confirmationToken,
        mfaToken,
        code,
        castleToken,
        // Forward the native signup handoff token (if present in the URL) so SIS
        // can skip reCAPTCHA, mirroring the password signup flow.
        ...(nativeHandoffSignupToken && { nativeHandoffSignupToken }),
      };

      const csrfToken = getState().csrfToken;

      const ENDPOINT_PATH = '/rest/login/social/confirmation';

      // These are used by SIS to redirect the user to the correct place.
      // In the "old" implementation (i.e. `submitFormHack`)
      const queryParams = queryString.stringify({
        // Only include the query parameter if the value exists
        ...(params.continue ? { continue: params.continue } : {}),
        ...(params.application ? { application: params.application } : {}),
      });

      // Only include the query parameters if any exist on the url
      const qs = queryParams.length > 0 ? `?${queryParams}` : '';

      const path = getGatedIdacBackendUrl(`${ENDPOINT_PATH}${qs}`);
      const res = await submitFormHackReplacement({
        method: 'POST',
        action: path,
        payload,
        csrfToken,
        enablePassingCookies: true,
      });

      responseStatus = res.status;
      const response = await res.json();

      const hasCreateAccountSpecificState = !!response.createAccount;

      // We have state from the backend, which we want to ensure is updated in the FE state.
      if (hasCreateAccountSpecificState) {
        // Want to make sure that it's the shape that we expect before updating the state
        const isCreateAccountObject =
          typeof response.createAccount === 'object' && !Array.isArray(response.createAccount);

        dispatch(
          confirmationRequestStateUpdate(isCreateAccountObject ? response.createAccount : {})
        );
      }

      const hasError = !!response?.createAccount?.formError;

      if (hasError) {
        dispatch(confirmationRequestFailed(response.createAccount.formError));
        return;
      }

      // This indicates the backend is not behaving as expected
      // since even in error cases, we expect the backend endpoint to return a 200
      if (!res.ok) {
        dispatch(confirmationRequestFailed('unknown'));
        return;
      }

      // The types on the FE define the response to always be under `createAccount` for this endpoint
      // but in practice the BE returns in some cases (i.e. success) in a different format
      // so we want to handle that
      const redirectTo: string | undefined =
        response?.createAccount?.redirectTo || response?.redirectTo;

      const hasRedirect = !!redirectTo;
      if (!hasRedirect) {
        // If we don't get a redirect URL from the BE _and_ we haven't been able to understand the error
        // we don't know where to send the user and there aren't enough details of the error (or they aren't in the expected format) so we are assuming the request failed.
        dispatch(confirmationRequestFailed('unknown'));
        return;
      }

      // Excluding `nativeHandoff` is what makes the retry work: the next submit finds no token and
      // takes the reCAPTCHA path instead of resending one SIS has already rejected.
      if (hasInvalidNativeSignupErrorCode(redirectTo)) {
        const updatedRedirectTo = restoreOriginalRedirectUrlParams({
          fullRedirectUrlValue: redirectTo,
          originalParams: new URLSearchParams(location.search),
          excludedParams: ['nativeHandoff'],
        });
        dispatch(confirmationRequestFailedWithRedirect('invalidNativeSignup', updatedRedirectTo));
        return;
      }

      dispatch(confirmationRequestSucceeded(redirectTo));
    } catch (error) {
      captureTaggedException(error, {
        capabilities: ['capability-group--signup', 'capability-name--signup'],
        extraProperties: {
          description: 'Failed to handle the social confirmation response',
          responseStatus,
        },
      });
      dispatch(confirmationRequestFailed('unknown'));
    }
  };

export const resendInHouseVerificationCode =
  (mfaToken: string): ThunkAction =>
  (dispatch, getState) => {
    const { csrfToken } = getState();

    const path = getGatedIdacBackendUrl(`/rest/mfa/resend?transactionToken=${mfaToken}`);

    return fetch(path, {
      method: 'POST',
      headers: {
        'X-CSRF-TOKEN': csrfToken,
      },
    }).then(res => {
      if (res.ok) {
        return res.json().then(json => {
          dispatch(resendSucceeded(json.transactionToken));
        });
      } else {
        return Promise.reject(
          new Error(`Failed to resend MFA code: ${res.status} ${res.statusText}`)
        );
      }
    });
  };
